"use client";

import { useState } from "react";
import {
  cosineSimilarity,
  euclideanDistance,
  manhattanDistance,
} from "@/lib/embedding-utils";
import exampleData from "@/data/embedding-examples.json";

type Metric = "cosine" | "euclidean" | "manhattan";
type Result = { text: string; embedding: number[] };

interface ExampleDataset {
  model: string | null;
  generatedAt: string | null;
  examples: Result[];
  sentenceExamples?: Result[];
  sentenceExamplesEn?: Result[];
}

const example = exampleData as ExampleDataset;

const deSentences = (example.sentenceExamples || []).map((item) => item.text);
const enSentences = (example.sentenceExamplesEn || []).map((item) => item.text);

const button =
  "min-h-11 min-w-11 rounded-lg border border-purple-500/30 px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-purple-500 disabled:opacity-50";
const provenance =
  "Beispielwerte – ändere die Begriffe und berechne deinen eigenen Vergleich.";

const english = {
  provenance:
    "Saved bilingual examples · model: Gemini. Change the texts to calculate a new comparison.",
  legend: "Compare texts",
  words: "Saved word examples",
  sentences: "Sentence examples",
  input: "Word or short sentence",
  remove: "Remove entry",
  calculate: "Calculate embeddings",
  calculating: "Calculating …",
  add: "+ Text",
  cloud: "New custom calculations send inputs to Google Gemini.",
  loading: "Generating new embeddings …",
  dirty: "Inputs changed. Calculate again to compare these texts.",
  title: "Compare similarity",
  metric: "Comparison metric",
  cosine: "Cosine similarity",
  euclidean: "Euclidean distance",
  manhattan: "Manhattan distance",
  dimensions: "Dimensions",
  higher: "Higher = more similar direction (−1 to +1).",
  lower: "Lower = smaller distance.",
  slider:
    "The slider compares only the first coordinates; it does not generate new embeddings.",
  pairs:
    "Pairs are ranked by similarity. Scores are not percentages and do not establish identical meaning.",
  empty: "Fill in every text or remove the empty field.",
  unavailable:
    "The calculation is unavailable. Please check your internet connection.",
  offline:
    "You are currently offline. Custom calculations require an internet connection to Gemini. The presets above work offline.",
  invalid: "The API returned invalid vectors. Please try again.",
  timeout: "The calculation took too long. Please try again.",
};

export default function EmbeddingPlayground({
  locale = "de",
}: {
  locale?: "de" | "en";
}) {
  const isEn = locale === "en";
  const initialSource = isEn ? english.provenance : provenance;

  const currentPresets = isEn
    ? [
        { label: english.words, texts: example.examples.map((item) => item.text) },
        {
          label: english.sentences,
          texts: enSentences.length ? enSentences : [
            "I sat on the bank of the river.",
            "I transferred money to my bank.",
            "The river bank is a peaceful place to sit.",
          ],
        },
      ]
    : [
        { label: "Wörter vergleichen", texts: example.examples.map((item) => item.text) },
        {
          label: "Sätze vergleichen",
          texts: deSentences.length ? deSentences : [
            "Die Bank am Fluss ist aus Holz.",
            "Ich überweise Geld an meine Bank.",
            "Auf der Holzbank am Ufer sitze ich gern.",
          ],
        },
      ];

  const [texts, setTexts] = useState(currentPresets[0].texts);
  const [results, setResults] = useState<Result[]>(example.examples);
  const [source, setSource] = useState(initialSource);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [metric, setMetric] = useState<Metric>("cosine");
  const [dimension, setDimension] = useState(
    example.examples[0].embedding.length,
  );

  const dirty =
    texts.length !== results.length ||
    texts.some((text, i) => text.trim() !== results[i]?.text);

  const score = (a: Result, b: Result) => {
    const left = a.embedding.slice(0, dimension);
    const right = b.embedding.slice(0, dimension);
    return metric === "cosine"
      ? cosineSimilarity(left, right)
      : metric === "euclidean"
        ? euclideanDistance(left, right)
        : manhattanDistance(left, right);
  };

  const pairs = results
    .flatMap((a, i) =>
      results
        .slice(i + 1)
        .map((b, j) => ({ left: i + 1, right: i + j + 2, value: score(a, b) })),
    )
    .sort((a, b) =>
      metric === "cosine" ? b.value - a.value : a.value - b.value,
    );

  function selectPreset(index: number) {
    const p = currentPresets[index];
    setTexts([...p.texts]);
    setError("");

    if (index === 0) {
      setResults(example.examples);
      setDimension(example.examples[0].embedding.length);
      setMetric("cosine");
      setSource(initialSource);
    } else if (index === 1) {
      const sentenceResults = isEn
        ? example.sentenceExamplesEn || example.sentenceExamples
        : example.sentenceExamples;
      if (sentenceResults && sentenceResults.length) {
        setResults(sentenceResults);
        setDimension(sentenceResults[0].embedding.length);
        setMetric("cosine");
        setSource(
          isEn
            ? "Saved sentence examples (Gemini)"
            : "Gespeicherte Satz-Beispiele (Gemini)",
        );
      }
    }
  }

  async function generate() {
    const submitted = texts.map((text) => text.trim());
    if (submitted.some((text) => !text)) {
      setError(
        isEn ? english.empty : "Bitte fülle alle Texte aus oder entferne das leere Feld.",
      );
      return;
    }

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setError(
        isEn
          ? english.offline
          : "Du bist offline. Für eigene Vergleiche wird eine Internetverbindung zu Gemini benötigt. Die Presets oben funktionieren offline.",
      );
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await fetch("/api/embeddings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ texts: submitted }),
        signal: AbortSignal.timeout(35000),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.error ||
            (isEn
              ? english.unavailable
              : "Die Berechnung ist gerade nicht verfügbar."),
        );
      }

      const vectors: unknown = data.embeddings;
      if (
        !Array.isArray(vectors) ||
        vectors.length !== submitted.length ||
        !vectors.every(
          (v) =>
            Array.isArray(v) &&
            v.length > 0 &&
            v.length === vectors[0].length &&
            v.every((n: unknown) => typeof n === "number" && Number.isFinite(n)),
        )
      ) {
        throw new Error(
          isEn
            ? english.invalid
            : "Die API hat keine gültigen Vektoren geliefert. Bitte versuche es erneut.",
        );
      }

      setResults(submitted.map((text, i) => ({ text, embedding: vectors[i] })));
      setDimension(vectors[0].length);
      setMetric("cosine");
      setSource(
        isEn
          ? `Newly calculated · Gemini · ${data.model || "gemini-embedding-2"}`
          : `Neu berechnet · Gemini · ${data.model || "gemini-embedding-2"}`,
      );
    } catch (cause) {
      setError(
        isEn
          ? cause instanceof Error && cause.name === "TimeoutError"
            ? english.timeout
            : cause instanceof Error
              ? cause.message
              : english.unavailable
          : cause instanceof Error && cause.name !== "TimeoutError"
            ? cause.message
            : "Die Berechnung dauert zu lange. Bitte versuche es erneut.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4 text-neutral-800 dark:text-neutral-200">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void generate();
        }}
      >
        <fieldset disabled={loading} className="space-y-3">
          <legend className="mb-2 font-semibold">
            {isEn ? english.legend : "Begriffe vergleichen"}
          </legend>

          <div className="flex flex-wrap gap-2">
            {currentPresets.map((preset, index) => (
              <button
                key={preset.label}
                type="button"
                className={button}
                onClick={() => selectPreset(index)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {texts.map((text, index) => (
              <div key={index} className="flex items-center gap-2">
                <label
                  htmlFor={`embedding-text-${index}`}
                  className="shrink-0 text-xs font-medium text-neutral-500"
                >
                  {index + 1}
                </label>
                <input
                  id={`embedding-text-${index}`}
                  aria-label={`${isEn ? english.input : "Begriff oder kurzer Satz"} ${index + 1}`}
                  value={text}
                  maxLength={1000}
                  required
                  placeholder={isEn ? english.input : "Begriff oder kurzer Satz"}
                  onChange={(event) =>
                    setTexts(
                      texts.map((item, i) =>
                        i === index ? event.target.value : item,
                      ),
                    )
                  }
                  className="h-11 min-w-0 flex-1 rounded-lg border border-neutral-300 bg-transparent px-3 text-sm focus-visible:outline-2 focus-visible:outline-purple-500 dark:border-neutral-700"
                />
                {texts.length > 2 && (
                  <button
                    type="button"
                    className={`${button} h-11 p-0`}
                    aria-label={
                      isEn
                        ? `${english.remove} ${index + 1}`
                        : `Eintrag ${index + 1} entfernen`
                    }
                    onClick={() =>
                      setTexts(texts.filter((_, i) => index !== i))
                    }
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="submit"
              className={`${button} bg-purple-700 text-white ${isEn ? "w-52 text-left" : ""}`}
            >
              {isEn
                ? loading
                  ? english.calculating
                  : english.calculate
                : loading
                  ? "Berechnet …"
                  : "Embeddings berechnen"}
            </button>
            {texts.length < 4 && (
              <button
                type="button"
                className={button}
                onClick={() => setTexts([...texts, ""])}
              >
                {isEn ? english.add : "+ Begriff"}
              </button>
            )}
          </div>

          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {isEn ? english.cloud : "Eigene Berechnungen senden deine Eingaben über unsere API an Google Gemini."}
          </p>
        </fieldset>
      </form>

      <div
        aria-live="polite"
        aria-atomic="true"
        className={`text-xs text-neutral-600 dark:text-neutral-400 ${isEn ? "min-h-40 sm:min-h-32" : ""}`}
      >
        {loading ? (
          <p role="status">
            {isEn ? english.loading : "Neue Embeddings werden erzeugt …"}
          </p>
        ) : (
          <p>{source}</p>
        )}
        {dirty && (
          <p className="mt-1 font-medium text-amber-600 dark:text-amber-400">
            {isEn
              ? english.dirty
              : "Eingaben geändert. Berechne erneut, um diese Begriffe zu vergleichen."}
          </p>
        )}
        {error && (
          <p role="alert" className="mt-1 text-red-700 dark:text-red-300">
            {error}
          </p>
        )}
      </div>

      <section aria-labelledby="embedding-results" className="space-y-3">
        <h3 id="embedding-results" className="font-semibold">
          {isEn ? english.title : "Ähnlichkeit vergleichen"}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="embedding-metric"
              className="mb-1 block text-xs font-medium"
            >
              {isEn ? english.metric : "Vergleichsmaß"}
            </label>
            <select
              id="embedding-metric"
              value={metric}
              onChange={(event) => setMetric(event.target.value as Metric)}
              className="h-11 w-full rounded-lg border border-neutral-300 bg-white px-3 text-sm focus-visible:outline-2 focus-visible:outline-purple-500 dark:border-neutral-700 dark:bg-neutral-900"
            >
              <option value="cosine">
                {isEn ? english.cosine : "Kosinus-Ähnlichkeit"}
              </option>
              <option value="euclidean">
                {isEn ? english.euclidean : "Euklidische Distanz"}
              </option>
              <option value="manhattan">
                {isEn ? english.manhattan : "Manhattan-Distanz"}
              </option>
            </select>
          </div>
          <div>
            <label
              htmlFor="embedding-dimensions"
              className="mb-1 block text-xs font-medium"
            >
              {isEn ? (
                <>
                  {english.dimensions}:{" "}
                  <span className="inline-block w-12 font-mono tabular-nums">
                    {dimension}
                  </span>
                  {" / "}
                  <span className="inline-block w-12 font-mono tabular-nums">
                    {results[0].embedding.length}
                  </span>
                </>
              ) : (
                <>
                  Dimensionen: {dimension} / {results[0].embedding.length}
                </>
              )}
            </label>
            <input
              id="embedding-dimensions"
              type="range"
              min={1}
              max={results[0].embedding.length}
              value={dimension}
              onChange={(event) => setDimension(Number(event.target.value))}
              className="h-11 w-full accent-purple-600"
            />
          </div>
        </div>

        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          {isEn
            ? metric === "cosine"
              ? english.higher
              : english.lower
            : metric === "cosine"
              ? "Höher = ähnlichere Richtung (−1 bis +1)."
              : "Niedriger = geringerer Abstand."}{" "}
          {isEn ? english.slider : "Der Regler vergleicht nur die ersten Koordinaten; er erzeugt keine neuen Embeddings."}
        </p>

        <div
          aria-live="polite"
          aria-busy={loading}
          className={`grid content-start gap-2 sm:grid-cols-2 ${isEn ? "min-h-[384px] sm:min-h-[192px]" : ""} ${dirty ? "opacity-60" : ""}`}
        >
          {pairs.map((pair, index) => (
            <div
              key={isEn ? index : `${pair.left}-${pair.right}`}
              className="rounded-lg border border-purple-500/25 px-3 py-2"
            >
              <div className="flex items-center justify-between gap-3">
                <p
                  className="min-w-0 truncate text-sm"
                  title={`${results[pair.left - 1].text} ↔ ${results[pair.right - 1].text}`}
                >
                  {results[pair.left - 1].text}{" "}
                  <span className="text-neutral-500">↔</span>{" "}
                  {results[pair.right - 1].text}
                </p>
                <span className="shrink-0 font-mono text-sm font-semibold text-purple-700 dark:text-purple-300">
                  {pair.value.toFixed(3)}
                </span>
              </div>
              {metric === "cosine" && (
                <div
                  aria-hidden="true"
                  className="mt-2 h-1 overflow-hidden rounded bg-neutral-200 dark:bg-neutral-800"
                >
                  <div
                    className="h-full bg-purple-500 transition-[width] duration-150 motion-reduce:transition-none"
                    style={{
                      width: `${(Math.max(-1, Math.min(1, pair.value)) + 1) * 50}%`,
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          {isEn
            ? english.pairs
            : "Paare sind nach Ähnlichkeit sortiert. Die Werte sind keine Prozente und beweisen keine gleiche Bedeutung."}
        </p>
      </section>
    </div>
  );
}
